import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  FormControlLabel,
  MenuItem,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogContentText,
  useMediaQuery,
  InputLabel,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CloseIcon from "@mui/icons-material/Close";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumberForDisplay,
} from "../../../Components/Validation/validationUtils";
import {
  useCreateTeamMutation,
  useDeleteEmployeeScheduleMutation,
  useGetDepartmentQuery,
  useGetPositionQuery,
} from "../../../Api/attoDeskApi";
import { FaTrash } from "react-icons/fa";
import AddIcon from "@mui/icons-material/Add";
import {
  IPosition,
  ITeam,
  ITeamCreate,
} from "../../../Api/Interface/api.interface";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import NewDepartement from "../../Department/Edit/index";
import { IDepartment } from "../../../Api/Interface/api.interface";
import Delete from "@mui/icons-material/Delete";
import NewPosition from "../../Position/Edit/index";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import useIsMobile from "../../../Core/hook/useIsMobile";
import TimeDropdown from "../../../Components/CustomTimePicker/TimeDropdown";

interface TimeSlot {
  id: number;
  startTime: string;
  endTime: string;
  hourlySalary: string;
}

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ITeam;
}

const toAllCaps = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.toUpperCase();
};

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [newCompany, { isLoading }] = useCreateTeamMutation();
  const [showPassword, setShowPassword] = useState(false);
  const isMobileRes = useIsMobile();

  const [openDialog, setOpenDialog] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<{
    index: number;
    slotIndex: number;
  } | null>(null);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const { data: departmentData, isLoading: departmentLoading } =
    useGetDepartmentQuery({
      searchText: "",
    });

  const { data: positionData, isLoading: positionLoading } =
    useGetPositionQuery({
      searchText: "",
    });

  const [openDepartment, setOpenDepartment] = useState(false);
  const [openPosition, setOpenPosition] = useState(false);

  const departmentList = useMemo(() => {
    return departmentData?.data as IDepartment[];
  }, [departmentData?.data]);

  const positionList = useMemo(() => {
    return positionData?.data as IPosition[];
  }, [positionData?.data]);

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik<ITeamCreate>({
    initialValues: {
      id: String(data?.id) || "",
      teamId: data?.teamId || "",
      teamPositionId: String(data?.teamPositionId) || "",
      teamHourlySalary: data?.teamHourlySalary || "",
      teamOtHourlySalary: data?.teamOtHourlySalary || "",
      teamDepartmentId: String(data?.teamDepartmentId) || "",
      teamMobileNum: data?.teamMobileNum
        ? formatPhoneNumberForDisplay(data.teamMobileNum)
        : "",
      teamFirstName: data?.teamFirstName || "",
      teamLastName: data?.teamLastName || "",
      teamAltMobileNum: data?.teamAltMobileNum
        ? formatPhoneNumberForDisplay(data.teamAltMobileNum)
        : "",
      teamImg: data?.teamImg || "",
      teamStreet: data?.teamStreet || "",
      teamCity: data?.teamCity || "",
      teamProvince: data?.teamProvince || "",
      teamPostalCode: data?.teamPostalCode || "",
      teamAddress: data?.teamAddress || "",
      teamEmail: data?.teamEmail || "",
      teamUserName: data?.teamUserName || "",
      teamPassWord: data?.teamPassWord || "",
      teamDescription: data?.teamDescription || "",
      emailReminder: Boolean(data?.emailReminder),
      smsReminder: Boolean(data?.smsReminder),
      availability: data?.availability?.map((available) => ({
        day: available.day,
        timeSlots: available.timeSlots?.map((slot) => ({
          id: slot.id,
          startTime: slot.startTime || "",
          endTime: slot.endTime || "",
          hourlySalary: slot.hourlySalary || "",
          spansNextDay: slot.spansNextDay || false,
        })) || [
          {
            id: null,
            startTime: "",
            endTime: "",
            hourlySalary: "",
            spansNextDay: false,
          },
        ],
      })) || [
        {
          day: "Sunday",
          timeSlots: [],
        },
        {
          day: "Monday",
          timeSlots: [],
        },
        {
          day: "Tuesday",
          timeSlots: [],
        },
        {
          day: "Wednesday",
          timeSlots: [],
        },
        {
          day: "Thursday",
          timeSlots: [],
        },
        {
          day: "Friday",
          timeSlots: [],
        },
        {
          day: "Saturday",
          timeSlots: [],
        },
      ],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: {
        availability?: {
          timeSlots?: {
            startTime?: string;
            endTime?: string;
            hourlySalary?: string;
          }[];
        }[];
      } = {};

      const toMinutes = (time: string, spansNextDay: boolean = false) => {
        const [h, m] = time.split(":").map(Number);
        let total = h * 60 + m;
        return spansNextDay ? total + 24 * 60 : total;
      };

      if (Array.isArray(values.availability)) {
        errors.availability = [];

        values.availability.forEach((day, dayIndex) => {
          if (!day.timeSlots || !Array.isArray(day.timeSlots)) return;

          const daySlotErrors: {
            startTime?: string;
            endTime?: string;
            hourlySalary?: string;
          }[] = [];

          // Validate individual slots
          day.timeSlots.forEach((slot, slotIndex) => {
            const slotErrors: {
              startTime?: string;
              endTime?: string;
            } = {};

            const isValidTime = (t: string) =>
              /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(t);

            if (!slot.startTime || !isValidTime(slot.startTime)) {
              slotErrors.startTime = "Start time is required (HH:mm)";
            }

            if (!slot.endTime || !isValidTime(slot.endTime)) {
              slotErrors.endTime = "End time is required (HH:mm)";
            }

            // if (
            //   slot.startTime &&
            //   slot.endTime &&
            //   isValidTime(slot.startTime) &&
            //   isValidTime(slot.endTime)
            // ) {
            //   const startMin = toMinutes(slot.startTime, slot.spansNextDay);
            //   const endMin = toMinutes(slot.endTime, slot.spansNextDay);

            //   if (endMin <= startMin) {
            //     slotErrors.endTime = "End time must be after start time";
            //   }
            // }

            if (
              slot.startTime &&
              slot.endTime &&
              isValidTime(slot.startTime) &&
              isValidTime(slot.endTime)
            ) {
              const startMin = toMinutes(slot.startTime); // don't shift start
              const endMin = toMinutes(slot.endTime, slot.spansNextDay); // shift end if needed

              if (endMin <= startMin) {
                slotErrors.endTime = "End time must be after start time";
              }
            }

            daySlotErrors[slotIndex] = Object.keys(slotErrors).length
              ? slotErrors
              : {};
          });

          // Check for overlaps
          for (let i = 0; i < day.timeSlots.length; i++) {
            const slotI = day.timeSlots[i];
            const startI = toMinutes(slotI.startTime!, slotI.spansNextDay);
            const endI = toMinutes(slotI.endTime!, slotI.spansNextDay);

            for (let j = i + 1; j < day.timeSlots.length; j++) {
              const slotJ = day.timeSlots[j];
              const startJ = toMinutes(slotJ.startTime!, slotJ.spansNextDay);
              const endJ = toMinutes(slotJ.endTime!, slotJ.spansNextDay);

              if (startI < endJ && startJ < endI) {
                daySlotErrors[i] = {
                  ...daySlotErrors[i],
                  startTime: "Overlaps with another slot",
                };
                daySlotErrors[j] = {
                  ...daySlotErrors[j],
                  startTime: "Overlaps with another slot",
                };
              }
            }
          }

          // Assign day errors if any slot has errors
          if (daySlotErrors.some((e) => Object.keys(e).length > 0)) {
            if (!errors.availability) {
              errors.availability = [];
            }
            errors.availability[dayIndex] = { timeSlots: daySlotErrors };
          }
        });

        if (!errors.availability.some((e) => e)) {
          delete errors.availability;
        }
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let teamImg: string | undefined = data?.teamImg;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("teamImg", selectedImage);
          teamImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const updatedData = {
          id: Number(values.id),
          teamId: values.teamId,
          teamPositionId: Number(values.teamPositionId),
          teamHourlySalary: values.teamHourlySalary,
          teamOtHourlySalary: values.teamOtHourlySalary,
          teamDepartmentId: Number(values.teamDepartmentId),
          teamMobileNum: values.teamMobileNum.replace(/-/g, ""),
          teamAltMobileNum: values.teamAltMobileNum.replace(/-/g, ""),
          teamFirstName: toProperCase(values.teamFirstName),
          teamLastName: toProperCase(values.teamLastName),
          teamCity: toProperCase(values.teamCity),
          teamAddress: values.teamAddress,
          teamEmail: values.teamEmail?.trim().toLowerCase(),
          teamDescription: values.teamDescription,
          teamImg,
          availability: values.availability,
          teamUserName: values.teamUserName,
          teamPassWord: values.teamPassWord,
          teamStreet: toProperCase(values.teamStreet),
          teamProvince: toProperCase(values.teamProvince),
          teamPostalCode: toAllCaps(values.teamPostalCode),
          emailReminder: values.emailReminder,
          smsReminder: values.smsReminder,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
          setSelectedImage(undefined);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleDeleteClick = (index: number, slotIndex: number) => {
    setSlotToDelete({ index, slotIndex });
    setOpenDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSlotToDelete(null);
  };

  console.log(formik.errors.availability);

  const formValid = useMemo(() => {
    const hasName =
      !!formik.values.teamFirstName?.trim() ||
      !!formik.values.teamLastName?.trim();

    return hasName;
  }, [
    formik.values.teamFirstName,
    formik.values.teamLastName,
    // formik.isValid,
    // formik.dirty,
  ]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const [deleteCategory] = useDeleteEmployeeScheduleMutation();

  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      const { index, slotIndex } = slotToDelete;
      const slotId = formik.values.availability[index].timeSlots[slotIndex].id;

      try {
        await deleteCategory(String(slotId));

        // Explicitly type timeSlots as TimeSlot[]
        const timeSlots = formik.values.availability[index]
          .timeSlots as TimeSlot[];

        // Filter out the deleted time slot, considering the possibility of `null` id
        formik.setFieldValue(
          `availability.${index}.timeSlots`,
          timeSlots.filter((slot, i) => i !== slotIndex) // Ensure that we remove the right slot by index
        );

        setOpenDialog(false); // Close dialog after deletion
      } catch (error) {
        console.error("Error deleting time slot:", error);
      }
    }
  };

  if (departmentLoading || positionLoading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
        classes={{ paper: "inherit" }}
        fullScreen={isMobileRes}
        fullWidth={isMobileRes}
        maxWidth="lg"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                    {data ? " Edit Schedule" : "Add"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {isMobileRes ? (
                    <>
                      {formik.values.availability.map((day, index) => (
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Card className={classes.card}>
                            <Grid
                              container
                              justifyContent={"space-between"}
                              spacing={5}
                              sx={{ padding: 2 }}
                              className={classes.titleGrid}
                            >
                              <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                pt={2}
                                alignContent={"center"}
                              >
                                <Typography className={classes.cardTitle}>
                                  {day.day}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Grid container spacing={3} p={"24px"}>
                              <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                key={day.day}
                              >
                                <Grid container spacing={2} alignItems="center">
                                  <FieldArray
                                    name={`availability.${index}.timeSlots`}
                                  >
                                    {({ push, remove }) => (
                                      <>
                                        <Grid
                                          item
                                          lg={12}
                                          md={12}
                                          sm={12}
                                          xs={12}
                                        >
                                          <Grid
                                            container
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                          >
                                            {formik.values.availability[
                                              index
                                            ].timeSlots.map(
                                              (slot, slotIndex) => (
                                                <Grid
                                                  item
                                                  lg={12}
                                                  md={12}
                                                  sm={12}
                                                  xs={12}
                                                  key={slotIndex}
                                                  sx={{
                                                    mb: 2,
                                                    p: 2,
                                                  }}
                                                  className={
                                                    classes.timeSchedule
                                                  }
                                                >
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                      <InputLabel
                                                        sx={{
                                                          pb: 1,
                                                          fontSize: "11px",
                                                        }}
                                                      >
                                                        Start Time
                                                      </InputLabel>
                                                      <TimeDropdown
                                                        value={
                                                          formik.values
                                                            .availability[index]
                                                            .timeSlots[
                                                            slotIndex
                                                          ].startTime
                                                        }
                                                        onChange={(time) =>
                                                          formik.setFieldValue(
                                                            `availability.${index}.timeSlots.${slotIndex}.startTime`,
                                                            time
                                                          )
                                                        }
                                                      />
                                                       {typeof formik.errors
                                                            .availability?.[
                                                            index
                                                          ] === "object" &&
                                                            Array.isArray(
                                                              (
                                                                formik.errors
                                                                  .availability?.[
                                                                  index
                                                                ] as any
                                                              ).timeSlots
                                                            ) && (
                                                              <Typography
                                                                color="error"
                                                                fontSize={13}
                                                              >
                                                                {
                                                                  (
                                                                    formik
                                                                      .errors
                                                                      .availability?.[
                                                                      index
                                                                    ] as any
                                                                  )
                                                                    ?.timeSlots?.[
                                                                    slotIndex
                                                                  ]?.startTime
                                                                }
                                                              </Typography>
                                                            )}

                                                      {/* <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="time"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.startTime`
                                                        )}
                                                        InputProps={{
                                                          sx: { fontSize: 14 },
                                                          inputProps: {
                                                            step: 300,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: { fontSize: 14 },
                                                        }}
                                                      /> */}
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                      <InputLabel
                                                        sx={{
                                                          pb: 1,
                                                          fontSize: "11px",
                                                        }}
                                                      >
                                                        End Time
                                                      </InputLabel>
                                                      <TimeDropdown
                                                        value={
                                                          formik.values
                                                            .availability[index]
                                                            .timeSlots[
                                                            slotIndex
                                                          ].endTime
                                                        }
                                                        onChange={(time) =>
                                                          formik.setFieldValue(
                                                            `availability.${index}.timeSlots.${slotIndex}.endTime`,
                                                            time
                                                          )
                                                        }
                                                      />
                                                       {typeof formik.errors
                                                            .availability?.[
                                                            index
                                                          ] === "object" &&
                                                            Array.isArray(
                                                              (
                                                                formik.errors
                                                                  .availability?.[
                                                                  index
                                                                ] as any
                                                              ).timeSlots
                                                            ) && (
                                                              <Typography
                                                                color="error"
                                                                fontSize={13}
                                                              >
                                                                {
                                                                  (
                                                                    formik
                                                                      .errors
                                                                      .availability?.[
                                                                      index
                                                                    ] as any
                                                                  )
                                                                    ?.timeSlots?.[
                                                                    slotIndex
                                                                  ]?.endTime
                                                                }
                                                              </Typography>
                                                            )}

                                                      {/* <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="time"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.endTime`
                                                        )}
                                                        InputProps={{
                                                          sx: { fontSize: 14 },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: { fontSize: 14 },
                                                        }}
                                                      /> */}
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                      <FormControlLabel
                                                        control={
                                                          <Checkbox
                                                            checked={
                                                              slot.spansNextDay ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              formik.setFieldValue(
                                                                `availability.${index}.timeSlots.${slotIndex}.spansNextDay`,
                                                                e.target.checked
                                                              )
                                                            }
                                                          />
                                                        }
                                                        label="Ends next day"
                                                        sx={{
                                                          fontSize: "11px",
                                                        }}
                                                      />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                      <InputLabel
                                                        sx={{
                                                          pb: "2px",
                                                          fontSize: "11px",
                                                        }}
                                                      >
                                                        Hourly Salary
                                                      </InputLabel>

                                                      <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.hourlySalary`
                                                        )}
                                                        InputProps={{
                                                          sx: { fontSize: 14 },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: { fontSize: 14 },
                                                        }}
                                                      />
                                                    </Grid>

                                                    <Grid
                                                      item
                                                      xs={12}
                                                      textAlign="right"
                                                    >
                                                      <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                          handleDeleteClick(
                                                            index,
                                                            slotIndex
                                                          )
                                                        }
                                                      >
                                                        <FaTrash />
                                                      </IconButton>
                                                    </Grid>
                                                  </Grid>
                                                </Grid>
                                              )
                                            )}
                                          </Grid>
                                        </Grid>

                                        <Grid
                                          item
                                          lg={12}
                                          md={12}
                                          sm={12}
                                          xs={12}
                                          display="flex"
                                          sx={{
                                            justifyContent: "flex-end",
                                            alignItems: "flex-end",
                                            textAlign: "end",
                                          }}
                                        >
                                          <Button
                                            variant="contained"
                                            // startIcon={<AddIcon />}
                                            className={classes.addButton}
                                            onClick={() =>
                                              push({
                                                id: null,
                                                startTime: "",
                                                endTime: "",
                                                spansNextDay: false,
                                                hourlySalary:
                                                  formik.values
                                                    .teamHourlySalary,
                                              })
                                            }
                                          >
                                            <AddIcon />
                                          </Button>
                                        </Grid>
                                      </>
                                    )}
                                  </FieldArray>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <>
                      {formik.values.availability.map((day, index) => (
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Card className={classes.card}>
                            <Grid
                              container
                              justifyContent={"space-between"}
                              spacing={5}
                              sx={{ padding: 2 }}
                              className={classes.titleGrid}
                            >
                              <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                pt={2}
                                alignContent={"center"}
                              >
                                <Typography className={classes.cardTitle}>
                                  {day.day} Schedule
                                </Typography>
                              </Grid>
                            </Grid>

                            <Grid container spacing={3} p={"24px"}>
                              <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                key={day.day}
                              >
                                <Grid container spacing={2} alignItems="center">
                                  <FieldArray
                                    name={`availability.${index}.timeSlots`}
                                  >
                                    {({ push, remove }) => (
                                      <>
                                        <Grid
                                          item
                                          lg={12}
                                          md={12}
                                          sm={12}
                                          xs={12}
                                        >
                                          <Grid
                                            container
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                          >
                                            <TableContainer>
                                              <Table>
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell
                                                      className={
                                                        classes.tablheaderCreate
                                                      }
                                                    >
                                                      Start Time
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tablheaderCreate
                                                      }
                                                    >
                                                      End Time
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tablheaderCreate
                                                      }
                                                    ></TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tablheaderCreate
                                                      }
                                                    >
                                                      Hourly Salary
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tablheaderCreate
                                                      }
                                                      sx={{
                                                        textAlign: "right",
                                                      }}
                                                    >
                                                      Action
                                                    </TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {formik.values.availability[
                                                    index
                                                  ].timeSlots.map(
                                                    (slot, slotIndex) => (
                                                      <TableRow key={slotIndex}>
                                                        <TableCell>
                                                          <TimeDropdown
                                                            value={
                                                              formik.values
                                                                .availability[
                                                                index
                                                              ].timeSlots[
                                                                slotIndex
                                                              ].startTime
                                                            }
                                                            onChange={(time) =>
                                                              formik.setFieldValue(
                                                                `availability.${index}.timeSlots.${slotIndex}.startTime`,
                                                                time
                                                              )
                                                            }
                                                          />
                                                          {typeof formik.errors
                                                            .availability?.[
                                                            index
                                                          ] === "object" &&
                                                            Array.isArray(
                                                              (
                                                                formik.errors
                                                                  .availability?.[
                                                                  index
                                                                ] as any
                                                              ).timeSlots
                                                            ) && (
                                                              <Typography
                                                                color="error"
                                                                fontSize={13}
                                                              >
                                                                {
                                                                  (
                                                                    formik
                                                                      .errors
                                                                      .availability?.[
                                                                      index
                                                                    ] as any
                                                                  )
                                                                    ?.timeSlots?.[
                                                                    slotIndex
                                                                  ]?.startTime
                                                                }
                                                              </Typography>
                                                            )}

                                                          {/* <TextField
                                                            placeholder="Start Time"
                                                            size="small"
                                                            type="time"
                                                            {...formik.getFieldProps(
                                                              `availability.${index}.timeSlots.${slotIndex}.startTime`
                                                            )}
                                                            className={
                                                              classes.textField
                                                            }
                                                            InputProps={{
                                                              sx: {
                                                                fontSize: 14,
                                                              },
                                                            }}
                                                            InputLabelProps={{
                                                              sx: {
                                                                fontSize: 14,
                                                              },
                                                            }}
                                                          /> */}
                                                        </TableCell>
                                                        <TableCell>
                                                          <TimeDropdown
                                                            value={
                                                              formik.values
                                                                .availability[
                                                                index
                                                              ].timeSlots[
                                                                slotIndex
                                                              ].endTime
                                                            }
                                                            onChange={(time) =>
                                                              formik.setFieldValue(
                                                                `availability.${index}.timeSlots.${slotIndex}.endTime`,
                                                                time
                                                              )
                                                            }
                                                          />
                                                          {typeof formik.errors
                                                            .availability?.[
                                                            index
                                                          ] === "object" &&
                                                            Array.isArray(
                                                              (
                                                                formik.errors
                                                                  .availability?.[
                                                                  index
                                                                ] as any
                                                              ).timeSlots
                                                            ) && (
                                                              <Typography
                                                                color="error"
                                                                fontSize={13}
                                                              >
                                                                {
                                                                  (
                                                                    formik
                                                                      .errors
                                                                      .availability?.[
                                                                      index
                                                                    ] as any
                                                                  )
                                                                    ?.timeSlots?.[
                                                                    slotIndex
                                                                  ]?.endTime
                                                                }
                                                              </Typography>
                                                            )}

                                                          {/* <TextField
                                                            placeholder="End Time"
                                                            size="small"
                                                            type="time"
                                                            {...formik.getFieldProps(
                                                              `availability.${index}.timeSlots.${slotIndex}.endTime`
                                                            )}
                                                            className={
                                                              classes.textField
                                                            }
                                                            InputProps={{
                                                              sx: {
                                                                fontSize: 14,
                                                              },
                                                            }}
                                                            InputLabelProps={{
                                                              sx: {
                                                                fontSize: 14,
                                                              },
                                                            }}
                                                          /> */}
                                                        </TableCell>
                                                        <TableCell>
                                                          <FormControlLabel
                                                            control={
                                                              <Checkbox
                                                                // checked={ Boolean(`availability.${index}.timeSlots.${slotIndex}.spansNextDay`)}
                                                                checked={
                                                                  formik.values
                                                                    .availability[
                                                                    index
                                                                  ].timeSlots[
                                                                    slotIndex
                                                                  ]
                                                                    .spansNextDay ||
                                                                  false
                                                                }
                                                                onChange={(e) =>
                                                                  formik.setFieldValue(
                                                                    `availability.${index}.timeSlots.${slotIndex}.spansNextDay`,
                                                                    e.target
                                                                      .checked
                                                                  )
                                                                }
                                                              />
                                                            }
                                                            label="Ends next day"
                                                          />
                                                        </TableCell>
                                                        <TableCell>
                                                          <TextField
                                                            placeholder="Hourly Salary"
                                                            size="small"
                                                            type="number"
                                                            {...formik.getFieldProps(
                                                              `availability.${index}.timeSlots.${slotIndex}.hourlySalary`
                                                            )}
                                                            className={
                                                              classes.textField
                                                            }
                                                            InputProps={{
                                                              sx: {
                                                                fontSize: 14,
                                                              },
                                                            }}
                                                            InputLabelProps={{
                                                              sx: {
                                                                fontSize: 14,
                                                              },
                                                            }}
                                                          />
                                                        </TableCell>
                                                        <TableCell>
                                                          <Grid
                                                            container
                                                            spacing={1}
                                                            sx={{
                                                              display: "flex",
                                                              justifyContent:
                                                                "flex-end",
                                                            }}
                                                          >
                                                            <Grid item>
                                                              <IconButton
                                                                color="error"
                                                                onClick={() =>
                                                                  handleDeleteClick(
                                                                    index,
                                                                    slotIndex
                                                                  )
                                                                }
                                                                // disabled={
                                                                //   formik.values
                                                                //     .availability[
                                                                //     index
                                                                //   ].timeSlots
                                                                //     .length === 1
                                                                // }
                                                              >
                                                                <FaTrash />
                                                              </IconButton>
                                                            </Grid>
                                                          </Grid>
                                                        </TableCell>
                                                      </TableRow>
                                                    )
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Grid>
                                        </Grid>

                                        <Grid
                                          item
                                          lg={12}
                                          md={12}
                                          sm={12}
                                          xs={12}
                                          display="flex"
                                          sx={{
                                            justifyContent: "flex-end",
                                            alignItems: "flex-end",
                                            textAlign: "end",
                                          }}
                                        >
                                          <Button
                                            variant="contained"
                                            // startIcon={<AddIcon />}
                                            className={classes.addButton}
                                            onClick={() =>
                                              push({
                                                id: null,
                                                startTime: "",
                                                endTime: "",
                                                spansNextDay: false,
                                                hourlySalary:
                                                  formik.values
                                                    .teamHourlySalary,
                                              })
                                            }
                                          >
                                            <AddIcon />
                                          </Button>
                                        </Grid>
                                      </>
                                    )}
                                  </FieldArray>
                                  {/* {formik.errors.availability &&
                                    Array.isArray(formik.errors.availability) &&
                                    formik.errors.availability[index] &&
                                    typeof formik.errors.availability[index] !==
                                      "string" &&
                                    Array.isArray(
                                      (formik.errors.availability[index] as any)
                                        .timeSlots
                                    ) &&
                                    (
                                      formik.errors.availability[index] as any
                                    ).timeSlots.some(
                                      (slotError: any) => !!slotError
                                    ) &&
                                    formik.values.availability[index].day ===
                                      formik.values.availability[index].day && ( // ✅ compare values
                                      <Grid item xs={12} pt={1} pl={2}>
                                        <Typography
                                          color="error"
                                          fontWeight="bold"
                                          fontSize={14}
                                        >
                                          ⚠ {day.day} - Please fix the errors
                                          below:
                                        </Typography>
                                        <ul
                                          style={{ margin: 0, paddingLeft: 20 }}
                                        >
                                          {(
                                            formik.errors.availability[
                                              index
                                            ] as any
                                          ).timeSlots.map(
                                            (slotError: any, slotIdx: number) =>
                                              slotError && (
                                                <li key={slotIdx}>
                                                  {slotError.startTime && (
                                                    <Typography
                                                      color="error"
                                                      fontSize={13}
                                                    >
                                                      Slot {slotIdx + 1} Start
                                                      Time:{" "}
                                                      {slotError.startTime}
                                                    </Typography>
                                                  )}
                                                  {slotError.endTime && (
                                                    <Typography
                                                      color="error"
                                                      fontSize={13}
                                                    >
                                                      Slot {slotIdx + 1} End
                                                      Time: {slotError.endTime}
                                                    </Typography>
                                                  )}
                                                  {slotError.hourlySalary && (
                                                    <Typography
                                                      color="error"
                                                      fontSize={13}
                                                    >
                                                      Slot {slotIdx + 1} Hourly
                                                      Salary:{" "}
                                                      {slotError.hourlySalary}
                                                    </Typography>
                                                  )}
                                                </li>
                                              )
                                          )}
                                        </ul>
                                      </Grid>
                                    )} */}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </>
                  )}
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveAltIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
                {openDepartment && (
                  <NewDepartement
                    handleCloseDialog={() => setOpenDepartment(false)}
                    openModel={openDepartment}
                  />
                )}

                {openPosition && (
                  <NewPosition
                    handleCloseDialog={() => setOpenPosition(false)}
                    openModel={openPosition}
                  />
                )}

                <Dialog
                  open={openDialog}
                  onClose={handleDeleteCancel}
                  sx={{ padding: 5, minWidth: 450 }}
                >
                  <Grid className={classes.Dialog}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Delete Time Slot
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <DialogContentText sx={{ pt: 3 }}>
                      Are you sure you want to delete this time slot?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleDeleteCancel}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<Delete />}
                          className={classes.addButton}
                          onClick={handleDeleteConfirm}
                        >
                          Confirm
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Dialog>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
